import type { AuthUser, InvitationPreview, OrganizationRef } from '@viewer/protocol';
import { useEffect, useState } from 'react';
import { api, errorText } from '../../lib/api/client';
import { LoginScreen } from '../auth/LoginScreen';
import { roleLabel } from '../org/labels';
import styles from '../org/org.module.css';

interface Props {
  token: string;
  user: AuthUser | null;
  onLogin(email: string, password: string): Promise<void>;
  onSignup(name: string, email: string, password: string): Promise<void>;
  onLogout(): Promise<void>;
  /** Convite aceito: leva para a empresa. */
  onAccepted(organization: OrganizationRef): void;
  onDismiss(): void;
}

export function InviteScreen({ token, user, onLogin, onSignup, onLogout, onAccepted, onDismiss }: Props) {
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<InvitationPreview>(`/api/invitations/${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => setError(errorText(err)));
  }, [token]);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      const org = await api.post<OrganizationRef>(`/api/invitations/${encodeURIComponent(token)}/accept`);
      onAccepted(org);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !preview) {
    return (
      <section className={styles.panel} style={{ width: 'min(440px, 100%)' }}>
        <h2 className={styles.panelTitle}>Convite indisponível</h2>
        <p className={styles.help}>{error}</p>
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onDismiss}>
          Ir para o início
        </button>
      </section>
    );
  }

  if (!preview) return <p className={styles.muted}>Carregando convite...</p>;

  const sameEmail = user && user.email.toLowerCase() === preview.email.toLowerCase();

  if (!user) {
    return (
      <LoginScreen
        onLogin={onLogin}
        onSignup={onSignup}
        fixedEmail={preview.email}
        intro={`${preview.invitedBy} convidou ${preview.email} para entrar em ${preview.organizationName} como ${roleLabel[preview.role].toLowerCase()}. Entre ou crie sua conta com este e-mail para aceitar.`}
      />
    );
  }

  return (
    <section className={styles.panel} style={{ width: 'min(440px, 100%)' }}>
      <h2 className={styles.panelTitle}>Convite para {preview.organizationName}</h2>
      <p className={styles.help}>
        {preview.invitedBy} convidou <strong>{preview.email}</strong> para participar como{' '}
        {roleLabel[preview.role].toLowerCase()}.
      </p>
      {sameEmail ? (
        <>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onDismiss} disabled={busy}>
              Agora não
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void accept()} disabled={busy}>
              {busy ? 'Entrando...' : 'Aceitar convite'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className={styles.error}>
            Você está logado como {user.email}. Este convite é para {preview.email}.
          </p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onDismiss}>
              Ir para o início
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void onLogout()}>
              Sair e entrar com outra conta
            </button>
          </div>
        </>
      )}
    </section>
  );
}
